import { useTranslation } from 'react-i18next';
import { Combobox } from '../form/combobox.jsx';
import { Radio } from '../form/radio.jsx';
import { TagPicker } from '../form/tag-picker.jsx';
import { Button } from './button.jsx';
import { deriveSelectValue } from './../../../../shared-web/form-utils/derive-select-value.js';

/**
 * Map option values to their label string for picker options.
 * @param {Array<string | { value: string, label: string }>} options
 * @returns {string[]}
 */
function optionLabels(options) {
	return options.map((o) => (typeof o === 'object' ? o.label : o));
}

/**
 * @param {{ id?: string, value?: string, required?: boolean, className?: string, onChange?: (value: string) => void, clearLabel: string }} props
 * @returns {JSX.Element}
 */
function renderDateField({ id, value, required, className, onChange, clearLabel }) {
	return (
		<div className="form-date-wrap">
			<input
				id={id}
				type="date"
				value={value}
				onChange={(e) => onChange?.(e.target.value)}
				className={className}
			/>
			{!required && value && (
				<Button
					className="form-date-clear"
					onClick={() => onChange?.('')}
					aria-label={clearLabel}
				>
					&#10005;
				</Button>
			)}
		</div>
	);
}

/**
 * @param {{ id?: string, value?: string, onChange?: (value: string) => void, min?: string | number, step?: string | number, disabled?: boolean, autoFocus?: boolean, placeholder?: string, className?: string }} props
 * @returns {JSX.Element}
 */
function renderNumberField({ id, value, onChange, min, step, disabled, autoFocus, placeholder, className }) {
	return (
		<input
			id={id}
			type="number"
			step={step ?? 'any'}
			min={min}
			value={value}
			onChange={(e) => onChange?.(e.target.value)}
			onKeyDown={(e) => {
				if (e.key === 'e' || e.key === 'E') { e.preventDefault(); }
			}}
			className={'text-field' + (className ? ' ' + className : '')}
			autoFocus={autoFocus}
			disabled={disabled}
			placeholder={placeholder}
		/>
	);
}

/**
 * @param {{ id?: string, value?: string, options?: Array<string | { value: string, label: string }>, onChange?: (value: string) => void, disabled?: boolean, title?: string, className?: string, t: import('i18next').TFunction }} props
 * @returns {JSX.Element}
 */
function renderSelectField({ id, value, options, onChange, disabled, title, className, t }) {
	const derivedValue = deriveSelectValue(options || [], value ?? '');
	const placeholder = t('field.select');
	return (
		<Combobox
			id={id}
			value={derivedValue}
			options={options || []}
			onChange={(v) => onChange?.(v)}
			selectOnly
			clearable
			emptyLabel={placeholder}
			placeholder={placeholder}
			disabled={disabled}
			title={title}
			className={className}
		/>
	);
}

/**
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} props.type
 * @param {string} [props.value]
 * @param {(value: string) => void} [props.onChange]
 * @param {Array<string | { value: string, label: string }>} [props.options]
 * @param {boolean} [props.required]
 * @param {string} [props.placeholder]
 * @param {string | number} [props.min]
 * @param {string | number} [props.step]
 * @param {boolean} [props.disabled]
 * @param {string} [props.title] — native tooltip/aria-description for select fields
 * @param {boolean} [props.autoFocus]
 * @param {string} [props.className]
 * @param {(name: string) => Promise<{ id: string, name: string }>} [props.onCreate]
 * @param {import('i18next').TFunction} props.t
 * @returns {JSX.Element | null}
 */
function renderTypedField({ id, type, value, onChange, options, required, placeholder, min, step, disabled, title, autoFocus, className, onCreate, t }) {
	if (type === 'combobox' || type === 'tags') {
		if (type === 'combobox') {
			return (
				<Combobox
					id={id}
					value={value ?? ''}
					options={optionLabels(options || [])}
					onChange={(v) => onChange?.(v)}
					placeholder={placeholder}
					className={className}
				/>
			);
		}
		return (
			<TagPicker
				id={id}
				value={value ?? ''}
				options={optionLabels(options || [])}
				onChange={(v) => onChange?.(v)}
				onCreate={onCreate}
				placeholder={placeholder}
				className={className}
			/>
		);
	}
	if (type === 'select') {
		return renderSelectField({ id, value, options, onChange, disabled, title, className, t });
	}
	if (type === 'number') {
		return renderNumberField({ id, value, onChange, min, step, disabled, autoFocus, placeholder, className });
	}
	if (type === 'date') {
		return renderDateField({ id, value, required, className, onChange, clearLabel: t('aria.clearDate') });
	}
	if (type === 'direction' || type === 'radio') {
		return (
			<Radio
				name={id ?? ''}
				value={value ?? ''}
				options={options ?? []}
				onChange={(val) => onChange?.(val)}
			/>
		);
	}
	return (
		<input
			id={id}
			type={type}
			className={'text-field' + (className ? ' ' + className : '')}
			value={value}
			onChange={(e) => onChange?.(e.target.value)}
			placeholder={placeholder}
			disabled={disabled}
			autoFocus={autoFocus}
		/>
	);
}

export function InputField({ id, type = 'text', value, onChange, options, required, placeholder, min, step, disabled, title, autoFocus, className, onCreate }) {
	const { t } = useTranslation();
	return renderTypedField({ id, type, value, onChange, options, required, placeholder, min, step, disabled, title, autoFocus, className, onCreate, t });
}
